/**
 * GeoDataService
 *
 * Database-backed service for loading geographical data from Supabase tables
 * and transforming it into SelectOption formats for cascading field dependencies.
 *
 * Uses hybrid approach:
 * - UUIDs as internal primary keys (database integrity)
 * - ISO codes (iso2, state_code) as reference values in application code
 *
 * Data Sources:
 * - geo_countries: Country data with iso2/iso3 codes, phone_code, currency, etc.
 * - geo_states: State/province data linked by country_id
 * - geo_cities: City data linked by state_id and country_id
 *
 * Created: January 2026
 */

import { supabase } from '@xanectech/supabase-client';
import type { SelectOption } from '@xanectech/types';
import { GeoCountry, GeoCity, GeoState } from '@xanectech/types';

import { createLogger } from '@xanectech/lib';

const logger = createLogger('Data - GeoDataService');

// Cache for loaded data
interface GeoCache {
  countries: GeoCountry[] | null;
  statesByCountry: Map<string, GeoState[]>;
  citiesByState: Map<string, GeoCity[]>;
  countryByIso2: Map<string, GeoCountry>;
  lastFetch: number | null;
}

const cache: GeoCache = {
  countries: null,
  statesByCountry: new Map(),
  citiesByState: new Map(),
  countryByIso2: new Map(),
  lastFetch: null,
};

// Cache TTL: 5 minutes
const CACHE_TTL = 5 * 60 * 1000;

/**
 * Check if cache is still valid
 */
const isCacheValid = (): boolean => {
  if (!cache.lastFetch) return false;
  return Date.now() - cache.lastFetch < CACHE_TTL;
};

export class GeoDataService {
  /**
   * Get all active countries
   */
  static async getCountries(): Promise<GeoCountry[]> {
    if (cache.countries && isCacheValid()) {
      return cache.countries;
    }

    const { data, error } = await supabase
      .from('geo_countries')
      .select('*')
      .eq('is_active', true)
      .order('name');

    if (error) {
      logger.error('GeoDataService: Error fetching countries:', error);
      return cache.countries || [];
    }

    // Cast data to GeoCountry[] - the database schema matches our interface
    cache.countries = (data as GeoCountry[]) || [];
    cache.lastFetch = Date.now();

    // Build iso2 lookup map
    cache.countryByIso2.clear();
    for (const country of cache.countries) {
      cache.countryByIso2.set(String(country.iso2).toUpperCase(), country);
    }

    return cache.countries;
  }

  /**
   * Get country by ISO2 code
   */
  static async getCountryByIso2(iso2: string): Promise<GeoCountry | undefined> {
    // Ensure countries are loaded
    await this.getCountries();
    return cache.countryByIso2.get(iso2.toUpperCase());
  }

  /**
   * Get country by ID
   */
  static async getCountryById(
    countryId: string,
  ): Promise<GeoCountry | undefined> {
    // Ensure countries are loaded
    const countries = await this.getCountries();
    return countries.find((c) => c.id === countryId);
  }

  /**
   * Get country emoji by dialing code
   */
  static async getCountryByDialingCode(
    dialingCode: string,
  ): Promise<GeoCountry | undefined> {
    // Ensure countries are loaded
    const countries = await this.getCountries();
    return countries.find(
      (c) => c.phone_code === dialingCode.replace(/^\+/, ''),
    );
  }

  /**
   * Get all states/provinces
   */
  static async getStates(): Promise<GeoState[]> {
    // Ensure countries are loaded
    await this.getCountries();

    const allStates: GeoState[] = [];
    for (const country of cache.countries || []) {
      const states: GeoState[] | Error = await this.getStatesByCountryCode(
        String(country.iso2),
      );
      if (states instanceof Error) continue;
      Array.prototype.forEach.call(states, (state: GeoState) =>
        allStates.push(state),
      );
    }
    return allStates;
  }

  /**
   * Get all cities
   */
  static async getCities(): Promise<GeoCity[]> {
    // Ensure countries are loaded
    await this.getCountries();

    const allCities: GeoCity[] = [];
    for (const country of cache.countries || []) {
      const states = await this.getStatesByCountryCode(String(country.iso2));
      for (const state of states) {
        const cities: GeoCity[] | Error = await this.getCitiesByStateId(
          String(state.id),
        );
        if (cities instanceof Error) continue;
        Array.prototype.forEach.call(cities, (city: GeoCity) => {
          allCities.push(city);
        });
      }
    }
    return allCities;
  }

  /**
   * Get Geo Stats
   */
  static async getGeoStats(): Promise<{
    total_countries: number;
    total_states: number;
    total_cities: number;
  }> {
    const countries = await this.getCountries();
    let total_states = 0;
    let total_cities = 0;

    for (const country of countries) {
      const states = await this.getStatesByCountryCode(String(country.iso2));
      total_states += states.length;

      for (const state of states) {
        const cities: GeoCity[] | Error = await this.getCitiesByStateId(
          String(state.id),
        );
        if (cities instanceof Error) continue;
        total_cities += cities.length;
      }
    }

    return {
      total_countries: countries.length,
      total_states,
      total_cities,
    };
  }

  /**
   * Get states/provinces for a country by country ID
   */
  static async getStatesByCountryId(countryId: string): Promise<GeoState[]> {
    // First get the country ISO2 from ID
    const country = (await this.getCountries()).find((c) => c.id === countryId);
    if (!country) {
      logger.warn(`GeoDataService: Country not found for ID: ${countryId}`);
      return [];
    }

    return this.getStatesByCountryCode(String(country.iso2));
  }

  /**
   * Get states/provinces for a country by ISO2 code
   */
  static async getStatesByCountryCode(
    countryIso2: string,
  ): Promise<GeoState[]> {
    const cacheKey = countryIso2.toUpperCase();

    if (cache.statesByCountry.has(cacheKey) && isCacheValid()) {
      return cache.statesByCountry.get(cacheKey)!;
    }

    // First get the country UUID from ISO2
    const country = await this.getCountryByIso2(countryIso2);
    if (!country) {
      logger.warn(`GeoDataService: Country not found for ISO2: ${countryIso2}`);
      return [];
    }

    const { data, error } = await supabase
      .from('geo_states')
      .select('*')
      .eq('country_id', country.id)
      .eq('is_active', true)
      .order('name');

    if (error) {
      logger.error('GeoDataService: Error fetching states:', error);
      return [];
    }

    const states: GeoState[] = (data ?? []) as GeoState[];
    cache.statesByCountry.set(cacheKey, states);

    return states;
  }

  /**
   * Get cities for a state by state ID
   */
  static async getCitiesByStateId(stateId: string): Promise<GeoCity[]> {
    if (cache.citiesByState.has(stateId) && isCacheValid()) {
      return cache.citiesByState.get(stateId)!;
    }

    const { data, error } = await supabase
      .from('geo_cities')
      .select('*')
      .eq('state_id', stateId)
      .eq('is_active', true)
      .order('name');

    if (error) {
      logger.error('GeoDataService: Error fetching cities:', error);
      return [];
    }

    const cities: GeoCity[] = (data ?? []) as GeoCity[];
    cache.citiesByState.set(stateId, cities);

    return cities;
  }

  /**
   * Get cities for a state by state name and country ISO2
   */
  static async getCitiesByStateName(
    countryIso2: string,
    stateName: string,
  ): Promise<GeoCity[]> {
    const states = await this.getStatesByCountryCode(countryIso2);
    const state = states.find(
      (s) => s.name.toLowerCase() === stateName.toLowerCase(),
    );

    if (!state) {
      logger.warn(
        `GeoDataService: State not found: ${stateName} in ${countryIso2}`,
      );
      return [];
    }

    return this.getCitiesByStateId(String(state.id));
  }

  // ============== SelectOption Formatters ==============

  /**
   * Get country options as SelectOption[] for dropdowns
   * Uses iso2 as the value (human-readable, interoperable)
   */
  static async getCountryOptions(): Promise<SelectOption[]> {
    const countries = await this.getCountries();

    return countries.map((country) => ({
      label: country.name,
      value: country.iso2, // ISO2 code as the value
      metadata: {
        type: 'country',
        emoji: country.emoji,
        iso3: country.iso3,
        phone_code: country.phone_code,
        currency: country.currency,
        currency_symbol: country.currency_symbol,
      },
    }));
  }

  /**
   * Get state/province options as SelectOption[] for dropdowns
   * Uses state name as the value (for display and storage)
   */
  static async getStateOptions(countryIso2?: string): Promise<SelectOption[]> {
    logger.debug('GeoDataService: Fetching state options for country:', {
      countryIso2,
    });

    if (!countryIso2) {
      return [];
    }

    const states = await this.getStatesByCountryCode(countryIso2);

    return states.map((state) => ({
      label: state.name,
      value: state.name, // State name as the value for readability
      dependsOn: 'country',
      dependentValue: countryIso2,
      metadata: {
        type: 'state',
        state_id: state.id, // Include UUID for city lookup
        state_code: state.state_code,
        country_iso2: countryIso2,
      },
    }));
  }

  /**
   * Get city options as SelectOption[] for dropdowns
   * Uses city name as the value
   */
  static async getCityOptions(
    countryIso2?: string,
    stateName?: string,
  ): Promise<SelectOption[]> {
    if (!countryIso2 || !stateName) {
      return [];
    }

    const cities = await this.getCitiesByStateName(countryIso2, stateName);

    return cities.map((city) => ({
      label: city.name,
      value: city.name, // City name as the value
      dependsOn: 'state',
      dependentValue: stateName,
      metadata: {
        type: 'city',
        city_id: city.id,
        country_iso2: countryIso2,
        state_name: stateName,
      },
    }));
  }

  // ============== Metadata Accessors ==============

  /**
   * Get dialing code for a country (with + prefix)
   */
  static async getDialingCode(
    countryIso2: string,
  ): Promise<string | undefined> {
    const country = await this.getCountryByIso2(countryIso2);
    if (!country?.phone_code) return undefined;

    // Ensure proper format with + prefix
    const code = country.phone_code.replace(/^\+/, '');
    return `+${code}`;
  }

  /**
   * Get currency info for a country
   */
  static async getCurrency(countryIso2: string): Promise<
    | {
        code: string;
        name: string;
        symbol: string;
      }
    | undefined
  > {
    const country = await this.getCountryByIso2(countryIso2);
    if (!country?.currency) return undefined;

    return {
      code: country.currency,
      name: country.currency_name || country.currency,
      symbol: country.currency_symbol || country.currency,
    };
  }

  /**
   * Get country emoji flag
   */
  static async getCountryEmoji(
    countryIso2: string,
  ): Promise<string | undefined> {
    const country = await this.getCountryByIso2(countryIso2);
    return country?.emoji || undefined;
  }

  /**
   * Get country emoji flag by dialing code
   */
  static async getCountryEmojibyDialingCode(
    dialingCode: string,
  ): Promise<string | undefined> {
    const country = await this.getCountryByDialingCode(dialingCode);
    return country?.emoji || undefined;
  }

  /**
   * Get full country metadata
   */
  static async getCountryMetadata(
    countryIso2: string,
  ): Promise<GeoCountry | undefined> {
    return this.getCountryByIso2(countryIso2);
  }

  // ============== Utility Methods ==============

  /**
   * Clear all cached data
   */
  static clearCache(): void {
    cache.countries = null;
    cache.statesByCountry.clear();
    cache.citiesByState.clear();
    cache.countryByIso2.clear();
    cache.lastFetch = null;
  }

  /**
   * Check if countries are loaded
   */
  static isDataLoaded(): boolean {
    return cache.countries !== null && isCacheValid();
  }

  /**
   * Get data statistics
   */
  static async getDataStats(): Promise<{
    countries: number;
    cachedStates: number;
    cachedCities: number;
    isLoaded: boolean;
  }> {
    const countries = await this.getCountries();
    return {
      countries: countries.length,
      cachedStates: cache.statesByCountry.size,
      cachedCities: cache.citiesByState.size,
      isLoaded: this.isDataLoaded(),
    };
  }

  /**
   * Update Country
   */
  static async updateCountry(
    countryId: string,
    updates: Partial<GeoCountry>,
  ): Promise<GeoCountry | Error> {
    const { data, error } = await supabase
      .from('geo_countries')
      .update(updates as Record<string, unknown>)
      .eq('id', countryId)
      .single();

    if (error) {
      logger.error('GeoDataService: Error updating country:', error);
      return new Error(String(error.message));
    }

    // Invalidate cache
    this.clearCache();

    return data as GeoCountry;
  }

  /**
   * Update State
   */
  static async updateState(
    stateId: string,
    updates: Partial<GeoState>,
  ): Promise<GeoState | Error> {
    const { data, error } = await supabase
      .from('geo_states')
      .update(updates as Record<string, unknown>)
      .eq('id', stateId)
      .single();

    if (error) {
      logger.error('GeoDataService: Error updating state:', error);
      return new Error(String(error.message));
    }

    // Invalidate cache
    this.clearCache();

    return data as GeoState;
  }

  /**
   * Update City
   */
  static async updateCity(
    cityId: string,
    updates: Partial<GeoCity>,
  ): Promise<GeoCity | Error> {
    const { data, error } = await supabase
      .from('geo_cities')
      .update(updates as Record<string, unknown>)
      .eq('id', cityId)
      .single();

    if (error) {
      logger.error('GeoDataService: Error updating city:', error);
      return new Error(String(error.message));
    }

    // Invalidate cache
    this.clearCache();

    return data as GeoCity;
  }
}
